import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  settings: Record<string, unknown>;
  created_at: string;
}

export function useOrganization() {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const { user } = useAuth();

  const fetchOrganization = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Check if user is a member of any organization
      const { data: membership, error: memberError } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      if (memberError) {
        console.error('Error checking membership:', memberError);
        setNeedsOnboarding(true);
        setLoading(false);
        return;
      }

      if (!membership) {
        // User has no organization - needs onboarding
        setNeedsOnboarding(true);
        setLoading(false);
        return;
      }
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', membership.organization_id)
        .single();

      if (orgError) throw orgError;

      setOrganization(org as Organization);
      setNeedsOnboarding(false);
    } catch (error) {
      console.error('Error fetching organization:', error);
      setNeedsOnboarding(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganization();
  }, [user]);

  return {
    organization,
    loading,
    needsOnboarding,
    refetch: fetchOrganization
  };
}
